import { P2Alert, P2Modal, P2TextDescription, P2TextLabel } from 'react-bootstrap-common-script';
import { Separator } from '../separator';
import { useCallback, useState } from 'react';
import NewInfoSvg from '../../assets/svg/new-info';
import MoreDetailsIcon from '../../assets/svg/more-details';
import { StageDProposedFinancialPlan } from '../../models/classes/stage-d/Review-Application.class';
import { Helpers } from '../../utils/helper';

interface ProposedPaymentAllocationSectionInterface {
  proposedFinancialPlanData?: StageDProposedFinancialPlan;
}

const ProposedPaymentAllocationSection = ({ proposedFinancialPlanData }: ProposedPaymentAllocationSectionInterface) => {
  const [modalVisible, setModalVisible] = useState(false);

  const closeModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  const totalFees =
    (proposedFinancialPlanData?.total_payments_required_conveyancing_fees ?? 0) +
    (proposedFinancialPlanData?.total_payments_required_property_registration_fees ?? 0) +
    (proposedFinancialPlanData?.total_payments_required_stamp_fees ?? 0);

  const totalPayableByCash = proposedFinancialPlanData?.proposed_payment_allocation_payable_cash ?? 0;
  const totalSolicitorFeeByCash = proposedFinancialPlanData?.total_solicitor_fee_by_cash ?? 0;

  return (
    <div className='polymer-stage-d-proposed-payment-allocation-container'>
      <P2TextDescription color='#333' title={'Proposed Payment Allocation'} />

      <div className='polymer-stage-d-financial-plan-table'>
        <div className='polymer-stage-d-financial-plan-table__header-row'>
          <p>Total Payable</p>
          <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.proposed_payment_allocation_total_payable)}</p>
        </div>
        <div className='polymer-stage-d-financial-plan-table__primary-row'>
          <div className='polymer-stage-d-financial-plan-table__link' onClick={() => setModalVisible(true)}>
            <a className='polymer-text-links'>What are you paying for</a>
            <i className='bi bi-chevron-right' style={{ color: '#085054', fontSize: 12 }}></i>
          </div>
        </div>
        <Separator variant='solid' />
        <div className='polymer-stage-d-financial-plan-table__primary-row polymer-stage-d-financial-plan-table__primary-row_payable-cpf'>
          <P2TextLabel label={'Payable by CPF'} />
          <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.proposed_payment_allocation_payable_cpf)}</p>
        </div>
        {proposedFinancialPlanData?.proposed_payment_allocation_payable_cpf_each.map((item) => {
          return (
            <div className='polymer-stage-d-financial-plan-table__sub-row'>
              <p>{item.name}</p>
              <p>{Helpers.amountToTwoDecimals(item.amount)}</p>
            </div>
          );
        })}
        {proposedFinancialPlanData?.proposed_payment_allocation_payable_loan !== 0 && (
          <div className='polymer-stage-d-financial-plan-table__primary-row polymer-stage-d-financial-plan-table__primary-row_payable-cpf'>
            <P2TextLabel label={'Payable by Loan'} />
            <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.proposed_payment_allocation_payable_loan)}</p>
          </div>
        )}
        <div className='polymer-stage-d-financial-plan-table__primary-row polymer-stage-d-financial-plan-table__primary-row_payable-cpf'>
          <P2TextLabel label={'Payable by Cash'} />
          <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.proposed_payment_allocation_payable_cash)}</p>
        </div>
      </div>

      {/* Batch-2 */}
      {totalSolicitorFeeByCash > 0 && (
        <div className={'polymer-current-owner-financial-plan-top-section-container__info-box pt-4 mb-4'}>
          <P2Alert
            withBorder
            variant={'info'}
            title={`Total cash payment required is ${Helpers.amountToTwoDecimals(totalPayableByCash + totalSolicitorFeeByCash)}`}
            text={`Total payment required will include payment by owner as shown above and payment by private lawyer of ${Helpers.amountToTwoDecimals(
              totalSolicitorFeeByCash
            )}`}
          />
        </div>
      )}

      <P2Modal
        isOpen={modalVisible}
        onCloseModal={closeModal}
        variant='default'
        title='Total Payments Required for Ownership Change'
        children={
          <>
            <div className='mobile-only'>
              <Separator color='#DEE2E6' variant='solid' />
            </div>
            <p className='mt-3'>Details of the total payments required are listed below. All amounts stated are inclusive of GST.</p>
            <div className='polymer-stage-d-financial-plan-table polymer-stage-d-financial-plan-table-modal'>
              <div className='polymer-stage-d-financial-plan-table__header-row'>
                <p>Total Payments Required</p>
                <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.total_payments_required)}</p>
              </div>
              <Separator variant='solid' />
              <div className='polymer-stage-d-financial-plan-table__primary-row polymer-stage-d-financial-plan-table__primary-row_payable-cpf'>
                <p>Outstanding Mortgage Loan</p>
                <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.total_payments_required_outstanding_mortgage_loan)}</p>
              </div>
              <div className='polymer-stage-d-financial-plan-table__primary-row polymer-stage-d-financial-plan-table__primary-row_payable-cpf'>
                <p>CPF Refund to Outgoing Owner(s)</p>
                <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.total_payments_required_cpf_refund)}</p>
              </div>
              <div className='polymer-stage-d-financial-plan-table__primary-row polymer-stage-d-financial-plan-table__primary-row_payable-cpf'>
                <p>Fees</p>
                <p>{Helpers.amountToTwoDecimals(totalFees)}</p>
              </div>
              <div className='polymer-stage-d-financial-plan-table__sub-row'>
                <p>Stamp Fees</p>
                <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.total_payments_required_stamp_fees)}</p>
              </div>
              <div className='polymer-stage-d-financial-plan-table__sub-row'>
                <p>Conveyancing Fees</p>
                <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.total_payments_required_conveyancing_fees)}</p>
              </div>
              <div className='polymer-stage-d-financial-plan-table__sub-row'>
                <p>Registration Fees</p>
                <p>{Helpers.amountToTwoDecimals(proposedFinancialPlanData?.total_payments_required_property_registration_fees)}</p>
              </div>
              {proposedFinancialPlanData?.others_fee.map((fee) => {
                return (
                  <div className='polymer-stage-d-financial-plan-table__sub-row'>
                    <p>{fee.fee_name}</p>
                    <p>{Helpers.amountToTwoDecimals(fee.amount)}</p>
                  </div>
                );
              })}
            </div>
            <div className='polymer-stage-d-financial-plan-paying-for'>
              <NewInfoSvg />
              <div>
                <P2TextLabel
                  bold={400}
                  label={'In addition, if you are taking a new HDB mortgage loan, you need to make the following supplementary payments:'}
                />
                <ul>
                  <li>
                    <P2TextLabel bold={400} label={'Interest on the new mortgage loan'} />
                  </li>
                  <li>
                    <P2TextLabel
                      delay={1500}
                      bold={400}
                      label={'Home Protection Scheme premiums'}
                      hasTooltip
                      tooltipContent={
                        <div>
                          <p>The premium is derived based on loan amount, repayment period, age, gender, and your share of cover.</p>
                          <a
                            className='tooltip-more-details'
                            href='https://www.cpf.gov.sg/member/home-ownership/protecting-against-losing-your-home'
                            target='_blank'
                            rel='noreferrer'>
                            <p>More Details </p>
                            <MoreDetailsIcon />
                          </a>
                        </div>
                      }
                    />
                  </li>
                </ul>
              </div>
            </div>
          </>
        }
      />
    </div>
  );
};

export default ProposedPaymentAllocationSection;
